import { Radio, type RadioChangeEvent } from 'antd';
import {
  ALL_FILTER_VALUE,
  type FilterOption,
  type FilterValue,
  type RegionalOverviewFilterType
} from '@features/regional-overview/regional-overview.models';
import { useTranslation } from 'react-i18next';
import { getSelectedText } from '../../filter.utils';
import FilterSection from '../filter-section/filter-section.component';

interface SingleSelectFilterProps<T extends RegionalOverviewFilterType> {
  filter: T;
  title: string;
  options: FilterOption[];
  translatable?: boolean;
  value: string | undefined;
  onFilterChanged: (filter: T, value: FilterValue) => void;
  defaultExpanded?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  dataTestID?: string;
}

const radioStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column'
};

const SingleSelectFilter = <T extends RegionalOverviewFilterType>({
  filter,
  title,
  options,
  translatable,
  value,
  onFilterChanged,
  defaultExpanded,
  disabled,
  disabledTooltip,
  dataTestID
}: SingleSelectFilterProps<T>) => {
  const [t] = useTranslation();

  const handleChange = (e: RadioChangeEvent) => {
    onFilterChanged(filter, e.target.value);
  };

  const isAllSelected = value === ALL_FILTER_VALUE;
  const selectedText = getSelectedText(t, isAllSelected, value ? 1 : 0);

  return (
    <FilterSection
      name={title ?? ''}
      selectedText={selectedText}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      defaultExpanded={defaultExpanded}
      dataTestId={dataTestID}>
      <Radio.Group style={radioStyle} onChange={handleChange} value={value}>
        {options.map(option => (
          <Radio key={option.value} value={option.value}>
            {translatable ? t(option.labelKey) : option.labelKey}
          </Radio>
        ))}
      </Radio.Group>
    </FilterSection>
  );
};

export default SingleSelectFilter;
